import detectorInfoPane from './detectorInfoPane';
import detectorPermissions from './detectorPermissions';
import detectorSettingsModal from './detectorSettingsModal';

import detectorV1New from './detectorV1New';
import detectorV1Edit from './detectorV1Edit';
import signalTypeService from './signalTypeService';
import valueFormatter from '../../common/ui/formatting/valueFormatterModule';
import { SCALING_UNIT_TYPES } from '../../../common/ui/formatting/scalingUnitTypes';
import { detectorMutingRuleBar } from './detectorMutingRuleBar';
import { apmOldWizardSunsetBanner } from './ApmOldWizardSunsetBanner';
import { entityHealthContent } from './entityHealth/EntityHealthContentWrapper';
import { entityTabNotificationIndicator } from './entityHealth/EntityTabNotificationIndicator';

angular
    .module('signalview.detector', [
        'signalview.chartbuilder',
        'sfx.api.v2',
        'sfx.ui',
        'sfx.data',
        'sfx.util',
        'signalboost',
        'signalview.sfGlobalNav',
        'signalview.detector.notification',
        'signalview.detector.rule',
        'signalview.detector.signal',
        'signalview.chartdisplay.shared',
        'signalview.snapshot',
        'signalview-utils.functions',
        'signalview-utils.signalflowv2',
        'signalflowv2',
        'signalview.userAnalytics',
    ])
    .component('detectorInfoPane', detectorInfoPane)
    .component('detectorPermissions', detectorPermissions)
    .component('detectorV1New', detectorV1New)
    .component('detectorV1Edit', detectorV1Edit)
    .component('detectorMutingRuleBar', detectorMutingRuleBar)
    .component('apmOldWizardSunsetBanner', apmOldWizardSunsetBanner)
    .component('entityHealthContentWrapper', entityHealthContent)
    .component('entityTabNotificationIndicator', entityTabNotificationIndicator)
    .service('detectorSettingsModal', detectorSettingsModal)
    .service('signalTypeService', signalTypeService)
    .run([
        '$window',
        'moment',
        function ($window, moment) {
            function getStringFromSafeString(val) {
                if (angular.isDefined(val)) {
                    return val.toString();
                }
                return val;
            }
            function equals(lvalueIn, rvalueIn) {
                const lvalue = getStringFromSafeString(lvalueIn);
                const rvalue = getStringFromSafeString(rvalueIn);
                if (lvalue === rvalue) {
                    return true;
                }
                const quotedLv = "'" + lvalue + "'";
                const quotedRv = "'" + rvalue + "'";
                return lvalue === quotedRv || quotedLv === rvalue;
            }
            // for testing validation only
            const helperFn = function () {
                return '';
            };
            ['emitter', 'forEachEntry', 'htmlNewLine', 'notEmpty'].forEach(function (helper) {
                $window.Handlebars.registerHelper(helper, helperFn);
            });

            // real helper
            $window.Handlebars.registerHelper('eq', function (lvalue, rvalue, options) {
                if (arguments.length >= 3 && equals(lvalue, rvalue)) {
                    return options.fn(this);
                }
                return options.inverse(this);
            });
            $window.Handlebars.registerHelper('neq', function (lvalue, rvalue, options) {
                if (arguments.length >= 3 && !equals(lvalue, rvalue)) {
                    return options.fn(this);
                }
                return options.inverse(this);
            });
            $window.Handlebars.registerHelper('is_empty', function (lvalueIn, options) {
                const lvalue = getStringFromSafeString(lvalueIn);
                if (!angular.isDefined(lvalue) || lvalue === 'None') {
                    return options.fn(this);
                }
                try {
                    const arr = JSON.parse(lvalue);
                    if (angular.isArray(arr) && !arr.length) {
                        return options.fn(this);
                    }
                } catch (e) {
                    // not json
                }
                return options.inverse(this);
            });

            $window.Handlebars.registerHelper('doubleFormat', function (value, options) {
                if (value === null || value === '' || typeof value === 'undefined') return value;

                const num = Number(value);
                if (isNaN(num)) return value;

                const hash = options && options.hash ? options.hash : {};
                const precision =
                    hash['precision'] !== undefined ? parseInt(hash['precision'], 10) : 2;

                if (isNaN(precision) || precision < 0) return String(value);

                // Use Intl.NumberFormat to avoid scientific notation (e.g. "1e+21")
                return new Intl.NumberFormat('en-US', {
                    useGrouping: false,
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision,
                }).format(num);
            });

            const SCALING_UNIT_MAP = new Map(
                Object.values(SCALING_UNIT_TYPES).map((u) => [u.toLowerCase(), u])
            );

            $window.Handlebars.registerHelper('unitFormat', function (value, unit) {
                if (value === null || value === '' || typeof value === 'undefined') return value;

                const num = Number(value);
                if (isNaN(num)) return value;

                const canonicalUnit =
                    typeof unit === 'string' ? SCALING_UNIT_MAP.get(unit.toLowerCase()) : undefined;

                if (canonicalUnit) {
                    return valueFormatter.formatScalingUnit(num, canonicalUnit);
                }

                const formatted = new Intl.NumberFormat('en-US', {
                    useGrouping: false,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(num);

                return formatted + ' ' + unit;
            });

            $window.Handlebars.registerHelper('dateTimeFormat', function (time, options) {
                if (time === null || time === '' || typeof time === 'undefined') return null;

                const hash = options && options.hash ? options.hash : {};
                const format = hash['format'];
                const timezone = typeof hash.timezone === 'string' ? hash.timezone.trim() : '';

                let m = moment(time);
                if (!m.isValid()) return null;

                if (timezone) {
                    const tzUpper = timezone.toUpperCase();
                    if (
                        tzUpper === 'UTC' ||
                        tzUpper === 'UT' ||
                        tzUpper === 'GMT' ||
                        tzUpper === 'Z'
                    ) {
                        m = m.utc();
                    } else {
                        // Supports: "+3", "-7", "+03:00", "UTC+3", "GMT-04:00"
                        const match = timezone.match(
                            /^(?:UTC|GMT|UT)?([+-])(\d{1,2})(?::?(\d{2}))?$/i
                        );
                        if (match) {
                            const sign = match[1] === '-' ? -1 : 1;
                            const hours = Number(match[2]);
                            const mins = match[3] ? Number(match[3]) : 0;
                            if ((hours < 14 || (hours === 14 && mins === 0)) && mins < 60) {
                                const total = sign * (hours * 60 + mins);
                                m = m.utcOffset(total);
                            }
                        } else {
                            if (typeof m.tz === 'function') {
                                m = m.tz(timezone);
                            }
                        }
                    }
                }

                switch (format) {
                    case 'iso':
                        return m.toISOString();
                    case 'full':
                        return m.format('MM/DD/YYYY hh:mm:ss a');
                    case 'year':
                        return m.format('YYYY');
                    default:
                        return m.toString();
                }
            });
        },
    ])
    .constant('PACKAGE_INFO', {
        package: 'signalfx',
        version: '0.0.1',
    });
